import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { api, ApiError } from '../../api/client';
import type { CiblePassation, PassationContenu } from '../../types';
import { formatDateCourt } from '../demande/format';
import { seancePasseeLe } from '../demande/parcours';
import { RIEN_DE_PARTICULIER, planReprise, prenom, qui_debute, type ResumeReprise } from './accueil';
import { BarreCollante, PanneauPassation, actionPrincipale, type EtatSauvegarde, type SeancePassation } from './PanneauPassation';
import { SectionMots, SectionPratique, SectionQuiDebute, type Voix } from './SectionsPassation';

/**
 * Onglet « Passation » (DECISIONS §37), en trois questions dans l'ordre — Qui débute ? · Un mot pour chacun·e · Côté pratique — et un panneau
 * « Il reste à faire » qui porte l'unique bouton. La personne absente écrit (enregistré tout seul) et transmet ; la remplaçante voit qui débute
 * dès l'ouverture, lit les textes une fois la passation transmise et marque « C'est lu, merci » ; la gestion voit et peut compléter.
 * `flushRef` : le parent y trouve de quoi enregistrer sans attendre avant d'ouvrir la fenêtre de transmission.
 * `completerRef` : la fenêtre de transmission y trouve de quoi amener au champ à compléter.
 */
export function OngletPassation({ demandeId, version, seance, flushRef, completerRef, onTransmettre, onChange }: {
  demandeId: number;
  /** Change quand la demande a bougé (après transmission) : le contenu est relu. */
  version: number;
  seance: SeancePassation;
  flushRef: MutableRefObject<(() => Promise<void>) | null>;
  completerRef: MutableRefObject<((cible: CiblePassation) => void) | null>;
  onTransmettre: () => void;
  onChange: () => void;
}) {
  const [contenu, setContenu] = useState<PassationContenu | null>(null);
  const [erreurChargement, setErreurChargement] = useState<string | null>(null);
  const [logistique, setLogistique] = useState('');
  const [remarques, setRemarques] = useState<Record<number, string>>({});
  const [sauvegarde, setSauvegarde] = useState<EtatSauvegarde>('repos');
  const [reprise, setReprise] = useState<ResumeReprise | null>(null);
  const [lectureEnCours, setLectureEnCours] = useState(false);
  const [erreurAction, setErreurAction] = useState<string | null>(null);
  // Une seule région « live » permanente : ce qui vient de se passer, dit une fois (jamais à chaque frappe).
  const [annonce, setAnnonce] = useState('');
  // Après une correction : le curseur retourne sur la ligne (ou, si elle a disparu, sur la liste) — jamais perdu dans la page.
  const aFocaliser = useRef<{ participantId: number } | null>(null);
  const dejaCharge = useRef(false);
  const transmiseAvant = useRef<string | null>(null);
  const prenomRempl = prenom(seance.remplacantNom) || 'Le/la remplaçant·e';
  const prenomAbs = prenom(seance.absentNom) || 'L’intervenant·e';

  // Dernières valeurs et ce qui reste à enregistrer : lus par l'enregistrement différé (pas de valeurs périmées dans la minuterie).
  const valeurs = useRef({ logistique: '', remarques: {} as Record<number, string>, premiere: {} as Record<number, boolean | null> });
  const aEnregistrer = useRef({ logistique: false, remarques: new Set<number>(), premiere: new Set<number>() });
  const minuterie = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enVol = useRef<Promise<void>>(Promise.resolve());

  const enregistrer = useCallback((): Promise<void> => {
    if (minuterie.current) { clearTimeout(minuterie.current); minuterie.current = null; }
    const { logistique: loge, remarques: rem, premiere } = aEnregistrer.current;
    if (!loge && rem.size === 0 && premiere.size === 0) return enVol.current;
    const corps: { logistique_texte?: string | null; remarques?: { participant_id: number; remarque?: string | null; premiere_fois?: boolean | null }[] } = {};
    const remIds = [...rem], premIds = [...premiere];
    if (loge) corps.logistique_texte = valeurs.current.logistique;
    const ids = [...new Set([...remIds, ...premIds])];
    if (ids.length > 0) {
      corps.remarques = ids.map((id) => ({
        participant_id: id,
        ...(rem.has(id) ? { remarque: valeurs.current.remarques[id] ?? '' } : {}),
        ...(premiere.has(id) ? { premiere_fois: valeurs.current.premiere[id] ?? null } : {}),
      }));
    }
    aEnregistrer.current = { logistique: false, remarques: new Set(), premiere: new Set() };
    setSauvegarde('en_cours');
    // Les enregistrements partent l'un après l'autre : le dernier texte tapé est toujours celui qui reste.
    enVol.current = enVol.current.then(async () => {
      try {
        await api.enregistrerPassationContenu(demandeId, corps);
        setSauvegarde('fait');
        setErreurAction(null);
        // Une correction « débute / ne débute pas ici » change les signaux affichés : on les relit (sans toucher aux textes en cours de frappe).
        if (premIds.length > 0) {
          const c = await api.getPassationContenu(demandeId).catch(() => null);
          if (c) chargerContenu(c);
        }
      } catch (err) {
        // Rien n'est perdu : le texte reste à l'écran et sera renvoyé à la prochaine frappe ou au prochain essai.
        if (loge) aEnregistrer.current.logistique = true;
        for (const id of remIds) aEnregistrer.current.remarques.add(id);
        for (const id of premIds) aEnregistrer.current.premiere.add(id);
        setSauvegarde('erreur');
        setErreurAction(err instanceof Error ? err.message : "L'enregistrement n'a pas abouti. Votre texte est gardé à l'écran : réessayez.");
      }
    });
    return enVol.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demandeId]);

  useEffect(() => { flushRef.current = enregistrer; return () => { flushRef.current = null; void enregistrer(); }; }, [enregistrer, flushRef]);

  function planifier() {
    if (minuterie.current) clearTimeout(minuterie.current);
    setSauvegarde('en_cours');
    minuterie.current = setTimeout(() => { void enregistrer(); }, 800);
  }

  function chargerContenu(c: PassationContenu) {
    setContenu(c);
    const rem: Record<number, string> = {};
    for (const p of c.participants ?? []) if (p.remarque) rem[p.participant_id] = p.remarque;
    // Ce qui est en cours de frappe (non enregistré) n'est jamais écrasé par une relecture.
    if (!aEnregistrer.current.logistique) { setLogistique(c.logistique_texte ?? ''); valeurs.current.logistique = c.logistique_texte ?? ''; }
    const fusion = { ...rem };
    for (const id of aEnregistrer.current.remarques) fusion[id] = valeurs.current.remarques[id] ?? '';
    setRemarques(fusion);
    valeurs.current.remarques = fusion;
  }

  useEffect(() => {
    let annule = false;
    setErreurChargement(null);
    api.getPassationContenu(demandeId)
      .then((c) => { if (!annule) chargerContenu(c); })
      .catch((err) => {
        if (annule) return;
        setErreurChargement(err instanceof ApiError && err.status === 404
          ? "La passation n'est pas accessible pour le moment."
          : err instanceof Error ? err.message : 'Impossible de charger la passation.');
      });
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demandeId, version]);

  function annoncer(texte: string) {
    setAnnonce('');
    setTimeout(() => setAnnonce(texte), 60);
  }

  // Le contenu vient d'être relu après une correction : le curseur revient sur la ligne de la personne (ou, à défaut, sur la liste).
  useEffect(() => {
    const f = aFocaliser.current;
    if (!f || !contenu) return;
    aFocaliser.current = null;
    const cible = document.querySelector<HTMLElement>(`[data-geste="${f.participantId}"]`)
      ?? document.querySelector<HTMLElement>(`[data-retiree="${f.participantId}"]`)
      ?? document.getElementById('pg-ajout-choix') ?? document.getElementById('pg-debute');
    cible?.focus();
  }, [contenu]);

  // La passation vient d'être transmise (le bouton a disparu) : le curseur va sur le panneau, qui le dit, et l'écran l'annonce.
  useEffect(() => {
    const maintenant = contenu?.transmise_at ?? null;
    if (dejaCharge.current && !transmiseAvant.current && maintenant) {
      document.getElementById('pg-panneau')?.focus();
      annoncer(`Passation transmise à ${prenomRempl}.`);
    }
    transmiseAvant.current = maintenant;
    if (contenu) dejaCharge.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contenu?.transmise_at]);

  // ── Gestes ────────────────────────────────────────────────────────────────
  function modifierLogistique(v: string) {
    setLogistique(v); valeurs.current.logistique = v; aEnregistrer.current.logistique = true; planifier();
  }
  function modifierRemarque(id: number, v: string) {
    setRemarques((r) => ({ ...r, [id]: v })); valeurs.current.remarques = { ...valeurs.current.remarques, [id]: v };
    aEnregistrer.current.remarques.add(id); planifier();
  }
  /** « Rien à signaler » : un vrai texte (« Rien de particulier. »), enregistré tout de suite ; le curseur reste dans le champ pour le préciser. */
  function rienASignaler(id: number) {
    modifierRemarque(id, RIEN_DE_PARTICULIER);
    void enregistrer();
    setTimeout(() => document.getElementById(`passation-remarque-${id}`)?.focus(), 0);
  }
  /** Corriger « débute dans l'atelier » : oui (ajout), non (« ne débute pas ici »), ou retour à ce que dit Eveil (null). */
  function corriger(id: number, valeur: boolean | null) {
    const nom = contenu?.participants?.find((p) => p.participant_id === id)?.nom ?? 'La personne';
    valeurs.current.premiere = { ...valeurs.current.premiere, [id]: valeur };
    aEnregistrer.current.premiere.add(id);
    aFocaliser.current = { participantId: id };
    annoncer(valeur === true ? `${nom} : ajouté·e à la liste de celles et ceux qui débutent.` : valeur === false ? `${nom} : ne débute pas dans l’atelier.` : `${nom} : retour à ce que dit Eveil.`);
    void enregistrer();
  }
  function reprendre() {
    const r = contenu?.reprise;
    if (!r) return;
    const plan = planReprise(r, valeurs.current.logistique, valeurs.current.remarques);
    if (plan.logistique) modifierLogistique(plan.logistique);
    for (const x of plan.remarques) modifierRemarque(x.participant_id, x.remarque);
    setReprise({ date: formatDateCourt(r.date_atelier), logistique: plan.logistique, remarques: plan.remarques });
    annoncer(`Reprise du ${formatDateCourt(r.date_atelier)} faite : à relire.`);
    void enregistrer();
  }
  /** Annule la reprise : ne vide que ce qui n'a pas été modifié depuis (jamais un texte que la personne a retravaillé). */
  function annulerReprise() {
    if (!reprise) return;
    if (reprise.logistique != null && valeurs.current.logistique === reprise.logistique) modifierLogistique('');
    for (const x of reprise.remarques) if (valeurs.current.remarques[x.participant_id] === x.remarque) modifierRemarque(x.participant_id, '');
    setReprise(null);
    annoncer('Reprise annulée.');
    void enregistrer();
  }
  /** Réessayer l'enregistrement : ce qui est à l'écran n'a jamais été perdu, il repart. */
  function reessayer() { void enregistrer(); }
  async function marquerLu() {
    setLectureEnCours(true);
    setErreurAction(null);
    try {
      await api.marquerPassationLue(demandeId);
      setContenu(await api.getPassationContenu(demandeId));
      onChange();
      document.getElementById('pg-panneau')?.focus();
      annoncer(`Marquée comme lue. ${prenomAbs} est prévenu·e.`);
    } catch (err) {
      setErreurAction(err instanceof Error ? err.message : "L'enregistrement n'a pas abouti. Réessayez.");
    } finally {
      setLectureEnCours(false);
    }
  }

  /** Amène à une section : elle défile jusqu'à l'écran et le curseur arrive dans le premier champ à remplir (jamais perdu). */
  function aller(id: string) {
    const el = document.getElementById(id);
    if (!el) return;
    const reduit = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduit ? 'auto' : 'smooth', block: 'start' });
    const champs = [...el.querySelectorAll<HTMLTextAreaElement>('textarea')];
    const cible = id === 'pg-mots' ? champs.find((t) => !t.value.trim()) ?? champs[0] : id === 'pg-pratique' ? champs[0] : undefined;
    (cible ?? el).focus({ preventScroll: true });
  }
  useEffect(() => {
    completerRef.current = (cible) => aller(cible === 'logistique' ? 'pg-pratique' : 'pg-mots');
    return () => { completerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completerRef]);

  // ── Rendu ─────────────────────────────────────────────────────────────────
  if (erreurChargement) return <div className="empty-state"><div className="empty-state-desc">{erreurChargement}</div></div>;
  if (!contenu) return <div className="text-sm text-ink-3" role="status">Chargement…</div>;
  // La personne désignée n'a pas encore répondu (DECISIONS §38) : la demande avance, la passation lui reste fermée jusqu'à sa confirmation.
  if (contenu.en_attente_confirmation) {
    // La séance a eu lieu sans réponse : on ne peut plus confirmer, la passation ne s'ouvrira plus (la gestion régularise la demande).
    if (seancePasseeLe(seance.jourIso)) {
      return (
        <div className="empty-state">
          <div className="empty-state-title">La passation n’est plus disponible</div>
          <div className="empty-state-desc">La séance a eu lieu sans que votre réponse soit enregistrée. L’équipe gestion régularise la demande.</div>
        </div>
      );
    }
    return (
      <div className="empty-state">
        <div className="empty-state-title">La passation s’ouvrira après votre confirmation</div>
        <div className="empty-state-desc">Confirmez d’abord ce remplacement : vous pourrez alors lire les remarques sur les participant·e·s et la passation de la personne absente.</div>
      </div>
    );
  }
  if (!contenu.ouverte) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">Passation non initiée</div>
        <div className="empty-state-desc">Elle démarre une fois le statut Tipee confirmé.</div>
      </div>
    );
  }

  const d = contenu.droits;
  const participants = contenu.participants ?? [];
  const moi = contenu.moi;
  // « Vous » pour l'intervenant·e absent·e ; son prénom pour la gestion (comme la carte « Prochaine étape ») et pour la remplaçante.
  const voix: Voix = {
    vous: !!moi?.est_absent && !moi.peut_gerer,
    prenomAbsent: prenom(seance.absentNom) || 'L’intervenant·e',
    prenomRemplacant: prenom(seance.remplacantNom) || 'Le/la remplaçant·e',
  };
  const nbDebutent = qui_debute(participants).length;
  const faits = participants.filter((p) => (remarques[p.participant_id] ?? '').trim() !== '').length;
  const sansContenu = !logistique.trim() && faits === 0;
  const offreReprise = d.ecrire && contenu.reprise && sansContenu ? { date: formatDateCourt(contenu.reprise.date_atelier), auteur: contenu.reprise.auteur_nom } : null;
  const avecParticipants = participants.length > 0;
  const action = actionPrincipale(contenu, voix, seance, { onTransmettre, onMarquerLu: () => void marquerLu(), lectureEnCours });
  const texteBarre = d.transmettre
    ? `${faits} mot${faits > 1 ? 's' : ''} sur ${participants.length} · côté pratique ${logistique.trim() ? '✓' : 'à écrire'}`
    : `Passation transmise${contenu.transmise_par_nom ? ` par ${contenu.transmise_par_nom}` : ''}.`;

  return (
    <>
      <div className="pg-grille">
        <div className="pg-colonne">
          {avecParticipants ? (
            <>
              <SectionQuiDebute participants={participants} voix={voix} ecrire={d.ecrire} numero={1} onCorriger={corriger} />
              <SectionMots participants={participants} remarques={remarques} voix={voix} ecrire={d.ecrire} lireTextes={d.lireTextes} numero={2}
                repris={reprise} onChange={modifierRemarque} onBlur={() => void enregistrer()} onRien={rienASignaler} />
            </>
          ) : (
            <section className="card pg-section" aria-labelledby="pg-sans-participants">
              <h3 className="card-title" id="pg-sans-participants">Participant·e·s</h3>
              <p className="pg-vide" style={{ margin: 0 }}>Aucun·e participant·e à afficher pour cette séance.</p>
            </section>
          )}
          <SectionPratique logistique={logistique} voix={voix} ecrire={d.ecrire} lireTextes={d.lireTextes} numero={avecParticipants ? 3 : undefined}
            offreReprise={offreReprise} repriseFaite={reprise} onChange={modifierLogistique} onBlur={() => void enregistrer()}
            onReprendre={reprendre} onAnnulerReprise={annulerReprise} />
        </div>
        <PanneauPassation contenu={contenu} seance={seance} voix={voix} action={action} nbDebutent={nbDebutent} motsTotal={participants.length} motsFaits={faits}
          pratiqueFaite={!!logistique.trim()} sauvegarde={sauvegarde} erreur={erreurAction} onAller={aller} onReessayer={reessayer} />
      </div>
      <BarreCollante action={action} texte={texteBarre} erreur={sauvegarde === 'erreur'} onReessayer={reessayer} />
      <div className="sr-only" role="status" aria-live="polite">{annonce}</div>
    </>
  );
}
